// Lazy per-year event files: events/<year>.json is fetched on first use and kept. A year the bake
// did not produce resolves to [] (with one warning), so a window spanning unbaked years still
// works. A year the events index lists but that failed to load rejects and is not kept, so the
// caller can say so and the next call asks again.

import type { WorldEvent } from '../core/schema.ts';
import type { Reader } from './fetch.ts';

type EventFile = WorldEvent[] | { events?: WorldEvent[] };

function eventsOf(file: EventFile | null): WorldEvent[] {
  if (!file) {
    return [];
  }
  if (Array.isArray(file)) {
    return file;
  }
  return file.events ?? [];
}

/** year → events, sorted by date. Concurrent calls for one year share a single fetch. `baked` are the years that should exist. */
export function eventLoader(reader: Reader, baked: ReadonlySet<number> = new Set()): (year: number) => Promise<WorldEvent[]> {
  const cache = new Map<number, Promise<WorldEvent[]>>();

  return (year) => {
    const hit = cache.get(year);
    if (hit) {
      return hit;
    }
    const p = reader.json<EventFile>(`events/${year}.json`).then((file) => {
      if (!file && baked.has(year)) {
        cache.delete(year);
        throw new Error(`data: events/${year}.json could not be loaded`);
      }
      const list = eventsOf(file).slice();
      list.sort((x, y) => (x.d < y.d ? -1 : x.d > y.d ? 1 : 0));
      return list;
    });
    cache.set(year, p);
    return p;
  };
}
